import { queueModeManager as queue } from "../components/sidebar/queueModeManager"
import type { CodeStore } from "../store/store"
import type { CodeNavigator } from "../routing"
import type { ComputerContextValue } from "../computers/context"

export interface ControlHost {
    store: CodeStore
    navigate: CodeNavigator
    computers: ComputerContextValue
    workspaceId?: string
    taskId?: string
}
export interface VisibleTask {
    nodeId: string
    workspaceId: string
    taskId: string
    title: string
}
export interface QueueRoute {
    taskId: string
    hold(): void
    next(): void
    skip(minutes: number): Promise<void>
    accepted(): boolean
}
let host: ControlHost | undefined
let queueRoute: QueueRoute | undefined
let visible: readonly VisibleTask[] = []
export function bindControlHost(value: ControlHost): () => void {
    host = value
    return () => {
        if (host === value) host = undefined
    }
}
export function controlHost(): ControlHost {
    if (!host) throw new Error("The app is reconnecting. Retry when the selected computer is ready.")
    return host
}
export function bindQueueRoute(value: QueueRoute): () => void {
    queueRoute = value
    return () => {
        if (queueRoute === value) queueRoute = undefined
    }
}
export function currentQueueRoute(taskId: string): QueueRoute | undefined {
    return queueRoute?.taskId === taskId ? queueRoute : undefined
}
export function publishVisibleTasks(tasks: readonly VisibleTask[]): () => void {
    visible = tasks
    return () => {
        if (visible === tasks) visible = []
    }
}
export function visibleTasks(): readonly VisibleTask[] {
    return visible
}

export function clientContext() {
    const host = controlHost()
    const current = host.taskId ? host.store.tasks.getTaskModel(host.taskId) : undefined
    return {
        nodeId: host.computers.state.activeNodeId,
        workspaceId: host.workspaceId,
        taskId: host.taskId,
        path: host.navigate.location.pathname,
        visibleTasks: visibleTasks()
            .slice(0, 100)
            .map((entry, index) => ({ number: index + 1, ...entry })),
        visibleTaskCount: visibleTasks().length,
        projects: host.store.repos.repos.map((repo) => ({ id: repo.id, name: repo.name })),
        queue: {
            active: queue.isActive,
            ready: queue.eligibleTaskCount,
            running: queue.runningTaskCount,
            limit: queue.maxConcurrent,
            waiting: queue.isWaitingForCapacity,
            advanceAtMs: queue.advanceAtMs,
            currentEligible: host.taskId ? queue.isTaskEligible(host.taskId) : false,
            browsingUnavailableTask: queue.isBrowsingUnavailableTask,
            focused: queue.isAppFocused,
            idle: queue.isIdle,
            timerRunning: queue.isTimerRunning,
        },
        current: current
            ? {
                  draft: current.input.value,
                  commands: current.input.commands.map(({ id, enabled }) => ({ id, enabled })),
                  queued: current.input.queuedTurns.map(({ id, label }) => ({ id, label })),
                  pane: current.tray.openTray,
              }
            : undefined,
    }
}
